"use client";

import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";
import { chapters, layers } from "@/lib/layers";
import { d, img } from "@/lib/site";

const STEP_MS = 2200;
const firstOf = chapters.map((_, c) => layers.findIndex((l) => l.ch === c));
const countOf = chapters.map((_, c) => layers.filter((l) => l.ch === c).length);

// "Schicht für Schicht von Hand": chapter patches, pinned workshop note and step photo with controls,
// autoplay on request (2.2 s per step), swipe on the photo and arrow keys.
export default function LayerShow() {
  const [i, setI] = useState(0);
  const [flip, setFlip] = useState(0);
  const [playing, setPlaying] = useState(false);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const cur = layers[i];

  const go = (k: number) => {
    const n = (k + layers.length) % layers.length;
    if (n === i) return;
    setI(n);
    setFlip((f) => f + 1);
  };
  const step = (delta: number) => {
    setPlaying(false);
    go(i + delta);
  };
  const pickChapter = (c: number, focus = false) => {
    setPlaying(false);
    go(firstOf[c]);
    if (focus) tabs.current[c]?.focus();
  };

  // autoplay: one step every 2.2 s, stops at the last step
  const running = playing && i < layers.length - 1;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setFlip((f) => f + 1);
      setI((old) => Math.min(old + 1, layers.length - 1));
    }, STEP_MS);
    return () => clearInterval(t);
  }, [running]);

  // no autoplay under reduced motion
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const stop = () => mq.matches && setPlaying(false);
    mq.addEventListener("change", stop);
    return () => mq.removeEventListener("change", stop);
  }, []);

  const togglePlay = () => {
    if (running) return setPlaying(false);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return step(1);
    if (i === layers.length - 1) go(0);
    setPlaying(true);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const onTab = (e.target as HTMLElement).getAttribute("role") === "tab";
    if (onTab) {
      const c = cur.ch;
      const keys: Record<string, number> = {
        ArrowLeft: c - 1,
        ArrowUp: c - 1,
        ArrowRight: c + 1,
        ArrowDown: c + 1,
        Home: 0,
        End: chapters.length - 1,
      };
      if (!(e.key in keys)) return;
      e.preventDefault();
      pickChapter((keys[e.key] + chapters.length) % chapters.length, true);
      return;
    }
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      step(e.key === "ArrowLeft" ? -1 : 1);
    }
  };

  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") swipe.current = { x: e.clientX, y: e.clientY };
  };
  const onUp = (e: PointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - s.y)) step(dx < 0 ? 1 : -1);
  };

  const fade = { animationName: flip ? "tfadeA" : "none" };

  return (
    <div className="lay" onKeyDown={onKey}>
      <div role="tablist" aria-label="Bereiche" className="lay-tabs">
        {chapters.map((c, n) => {
          const sel = n === cur.ch;
          const dark = n === 1;
          return (
            <button
              key={c.name}
              ref={(el) => {
                tabs.current[n] = el;
              }}
              type="button"
              role="tab"
              id={`lay-tab-${n}`}
              aria-selected={sel}
              aria-controls="lay-panel"
              tabIndex={sel ? 0 : -1}
              onClick={() => pickChapter(n)}
              className="lay-ch"
              style={{ "--chink": c.ink, "--pt": `url(${d(c.patch)})` } as CSSProperties}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={d(c.nail)} alt="" width={44} height={38} />
              <span>
                <strong>{c.name}</strong>
                <span className="sr-only">
                  , {countOf[n]} {countOf[n] === 1 ? "Schritt" : "Schritte"}
                </span>
                <span className="lay-dots" aria-hidden="true">
                  {Array.from({ length: countOf[n] }, (_, s) => {
                    const k = firstOf[n] + s;
                    const bg =
                      k === i
                        ? c.ink
                        : k < i
                          ? dark
                            ? "rgba(43,36,22,.75)"
                            : "rgba(255,246,223,.85)"
                          : dark
                            ? "rgba(43,36,22,.3)"
                            : "rgba(255,246,223,.35)";
                    return <span key={k} className="lay-d" style={{ width: k === i ? 18 : 9, background: bg }} />;
                  })}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="cl" id="lay-panel" role="tabpanel" aria-labelledby={`lay-tab-${cur.ch}`}>
        <div className="lay-photo">
          <div className="lay-frame" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => (swipe.current = null)}>
            {layers.map((l, k) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={k}
                src={d(l.img)}
                alt={k === i ? `${l.name}, ${chapters[l.ch].name}` : ""}
                aria-hidden={k !== i}
                width={250}
                height={250}
                loading="lazy"
                draggable={false}
                style={{ opacity: k === i ? 1 : 0 }}
              />
            ))}
          </div>
          <div className="lay-ctl mt-5 flex items-center justify-center gap-2">
            <button type="button" onClick={() => step(-1)} aria-label="Vorheriger Schritt" className="lay-btn">
              ←
            </button>
            <button type="button" onClick={togglePlay} aria-label={running ? "Pause" : "Abspielen"} className="lay-btn">
              <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d={running ? "M6 5h4v14H6zM14 5h4v14h-4z" : "M8 5.5v13l10.5-6.5z"} />
              </svg>
            </button>
            <button type="button" onClick={() => step(1)} aria-label="Nächster Schritt" className="lay-btn">
              →
            </button>
          </div>
        </div>

        <div aria-live="polite" className="htag-wrap">
          <div className="htag">
            <span className="htag-hole bg-bg" />
            <div className="tag-head" style={{ textAlign: "left", paddingBottom: 10 }}>
              Werkstattzettel
            </div>
            {/* every name and note sits in one grid cell (only the current one visible), so the note keeps the size of the longest */}
            <div className="tag-row">
              <span className="tag-k">Schicht</span>
              <span className="grid">
                {layers.map((l, k) => (
                  <span
                    key={k}
                    className={`tag-v [grid-area:1/1] ${k === i ? "tfade" : "invisible"}`}
                    style={{ ...(k === i ? fade : null), fontSize: 28 }}
                    aria-hidden={k !== i}
                  >
                    {l.name}
                  </span>
                ))}
              </span>
            </div>
            <div className="tag-row" style={{ borderBottom: 0, paddingBottom: 0 }}>
              <span className="tag-k">Notiz</span>
              <span className="grid">
                {layers.map((l, k) => (
                  <span key={k} className={`tag-v [grid-area:1/1] ${k === i ? "tfade" : "invisible"}`} style={k === i ? fade : undefined} aria-hidden={k !== i}>
                    {l.text}
                  </span>
                ))}
              </span>
            </div>
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img className="htag-pin" src={d(img.pin)} alt="" width={36} height={36} />
        </div>
      </div>
    </div>
  );
}
